/**
 * TASK ATTACHMENTS — the upload flow and the pre-upload refusal, and nothing
 * else.
 *
 * ⛔ **THE SIZE AND TYPE RULES ARE NOT HERE.** They live in
 * `@/lib/company-attachments` and are imported, never restated: a task and a
 * company store their files in the same bucket under the same server-side
 * allow-list, so a second copy of the limit or the type table would agree the
 * day it was written and drift on the first change — and the drift is a file
 * one surface takes and the other refuses.
 *
 * ## The upload is THREE requests, and only two of them are our API
 *
 * ```
 * 1. createTaskAttachmentUploadTarget   signs a URL, creates NO row
 * 2. uploadToSignedUrl                  a bare PUT straight to object storage
 * 3. createTaskAttachment               records the file row
 * ```
 *
 * 🔴 **A FAILURE AT STEP 3 MEANS THE BYTES ARE ALREADY STORED.** The error
 * thrown from that step says so in its own words, because the reader's natural
 * reading of "it failed" is that nothing left the machine — and that is not
 * true. Retrying from scratch is safe (it uploads under a fresh key) but
 * leaves the first copy in storage with nothing pointing at it.
 *
 * ⚠️ `contentType` is captured ONCE from `File.type` and the same string is
 * used for the signature and for the PUT. The server pins `content-type` into
 * the signature, so reading `file.type` a second time is the tempting version
 * and a charset parameter appended between the two is a 403 from storage that
 * reads as a broken link.
 */

import { api } from './api';
import {
  attachmentContentType,
  isAllowedAttachmentType,
  MAX_ATTACHMENT_BYTES,
  oversizeRefusal,
  typeRefusal,
} from './company-attachments';
import type { TaskAttachment } from './types';

/**
 * The refusal sentence for a file that cannot be attached, or `null` when it
 * may be tried.
 *
 * ⚠️ **A courtesy, not the rule.** It runs before any bytes are spent so a
 * reader is not made to wait for a refusal the server would give anyway; the
 * server re-checks both and is the authority when the two disagree.
 *
 * ⚠️ An EMPTY `file.type` (a file the browser could not identify) is refused,
 * which is the intended direction: an unknown type resolved as allowed would be
 * a client widening a rule it does not own. The refusal names the way round it.
 */
export function validateTaskAttachmentFile(file: File): string | null {
  if (file.size > MAX_ATTACHMENT_BYTES) return oversizeRefusal(file.name, file.size);
  if (!isAllowedAttachmentType(attachmentContentType(file))) return typeRefusal(file.name);
  return null;
}

/**
 * Uploads one file to a task and records it. Throws on any failure, including
 * a refusal from {@link validateTaskAttachmentFile}, so nothing is requested
 * for a file this app already knows the server will refuse.
 *
 * ⚠️ **The create response is the WHOLE list**, as on the company resource. The
 * row this call made is the newest — the highest id — because ids are allocated
 * in insertion order.
 */
export async function uploadTaskAttachmentFile(
  taskId: number,
  file: File,
  signal?: AbortSignal,
): Promise<TaskAttachment> {
  const refusal = validateTaskAttachmentFile(file);
  if (refusal !== null) throw new Error(refusal);

  const contentType = attachmentContentType(file);

  const target = await api.createTaskAttachmentUploadTarget(
    taskId,
    { fileName: file.name, contentType, sizeBytes: file.size },
    signal,
  );

  await api.uploadToSignedUrl(target.data.uploadUrl, file, contentType, signal);

  let answer;
  try {
    answer = await api.createTaskAttachment(
      taskId,
      { kind: 'file', storageKey: target.data.storageKey, fileName: file.name, title: file.name },
      signal,
    );
  } catch (caught) {
    if (signal?.aborted) throw caught;
    const reason = caught instanceof Error && caught.message !== '' ? ` ${caught.message}` : '';
    /*
     * 🔴 The bytes are up. Saying "the upload failed" here would be false, and
     * it is the sentence a reader would otherwise infer.
     */
    throw new Error(
      `${file.name.trim() || 'The file'} finished uploading but could not be recorded on the task.${reason}`,
      { cause: caught },
    );
  }

  let newest: TaskAttachment | undefined;
  for (const row of answer.data) {
    if (newest === undefined || row.id > newest.id) newest = row;
  }
  if (newest === undefined) {
    throw new Error('The file was recorded but the task answered with no attachments.');
  }
  return newest;
}
